<template>
    <div class="phone-mockup">
        <div class="phone-header">
            <span class="carrier">{{ carrier }}</span>
            <span class="time">{{ time }}</span>
            <span class="battery">{{ battery }}</span>
        </div>

        <div class="screen-wrapper">
            <transition
                name="phone-slide"
                mode="out-in"
            >
                <div
                    v-if="current_mode === 'messages'"
                    key="messages"
                    class="screen-content"
                >
                    <div
                        class="message-header"
                        v-if="show_message_header"
                    >
                        <h4>{{ business_name }}</h4>
                        <span class="phone-number">{{ phone_number }}</span>
                    </div>

                    <div
                        class="message-content"
                        data-scroll-container
                    >
                        <slot name="messages"></slot>
                    </div>
                </div>

                <div
                    v-else-if="current_mode === 'browser'"
                    key="browser"
                    class="screen-content"
                >
                    <div class="browser-header">
                        <div class="browser-controls">

                            <div class="address-bar">
                                <v-icon
                                    size="14"
                                    class="lock-icon"
                                >mdi-lock</v-icon>
                                <span class="url">{{ browser_url }}</span>
                            </div>
                        </div>
                    </div>

                    <div
                        class="browser-content"
                        data-scroll-container
                    >
                        <transition
                            name="browser-content-slide"
                            mode="out-in"
                        >
                            <div :key="browser_content_key">
                                <slot name="browser"></slot>
                            </div>
                        </transition>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
export default {
    name: 'MockPhone',

    props: {
        carrier: {
            type: String,
            default: 'Verizon'
        },

        time: {
            type: String,
            default: '2:34 PM'
        },

        battery: {
            type: String,
            default: '87%'
        },

        business_name: {
            type: String,
            default: 'Business Name'
        },

        phone_number: {
            type: String,
            default: '(555) 123-4567'
        },

        show_message_header: {
            type: Boolean,
            default: true
        },

        mode: {
            type: String,
            default: 'messages',
            validator: (value) => ['messages', 'browser'].includes(value)
        },

        browser_url: {
            type: String,
            default: 'barrelcreek.com'
        },

        browser_content_key: {
            type: [String, Number],
            default: 'default'
        }
    },

    data() {
        return {
            current_mode: 'messages',
        }
    },

    mounted() {
        this.current_mode = this.mode;
    },

    watch: {
        mode: {
            handler(newMode) {
                this.current_mode = newMode;
            },
            immediate: true
        }
    },

    updated() {
        this.$nextTick(() => {
            const browserContent = this.$el.querySelector('.browser-content');
            if (browserContent && this.current_mode === 'browser') {
                browserContent.scrollTop = 0;
            }
        });
    },
}
</script>

<style lang="scss" scoped>
.phone {
    &-mockup {
        max-width: 390px;
        width: min(390px, 100%);
        height: 720px;
        margin: 0 auto;
        background: #1a1a1a;
        border-radius: 2.5rem;
        padding: 0.5rem;
        box-shadow:
            0 0 0 2px #333,
            0 8px 32px rgba(0, 0, 0, 0.4),
            inset 0 1px 0 rgba(255, 255, 255, 0.1);
        position: relative;

        // island
        &::before {
            content: '';
            position: absolute;
            top: 0.75rem;
            left: 50%;
            transform: translateX(-50%);
            width: 120px;
            height: 25px;
            background: #000;
            border-radius: 15px;
            z-index: 10;
        }

        // home indicator
        &::after {
            content: '';
            position: absolute;
            bottom: 0.5rem;
            left: 50%;
            transform: translateX(-50%);
            width: 140px;
            height: 4px;
            background: rgba(255, 255, 255, 0.3);
            border-radius: 2px;
        }
    }

    &-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        color: white;
        font-size: 0.8rem;
        font-weight: 600;
        padding: 2rem 1rem 0.5rem;
        position: relative;
        z-index: 5;

        .carrier {
            font-weight: 500;
        }

        .time {
            position: absolute;
            left: 50%;
            transform: translateX(-50%);
            font-weight: 600;
        }

        .battery {
            display: flex;
            align-items: center;
            position: relative;

            &::after {
                content: '';
                display: inline-block;
                width: 22px;
                height: 11px;
                border: 1px solid white;
                border-radius: 2px;
                margin-left: 4px;
                position: relative;

                &::before {
                    content: '';
                    position: absolute;
                    right: -3px;
                    top: 3px;
                    width: 2px;
                    height: 5px;
                    background: white;
                    border-radius: 0 1px 1px 0;
                }
            }

            &::before {
                content: '';
                position: absolute;
                right: 6px;
                top: 50%;
                transform: translateY(-50%);
                width: 16px;
                height: 10px;
                background: white;
                border-radius: 1px;
            }
        }
    }
}

.screen {
    &-wrapper {
        background: white;
        border-radius: 1.2rem;
        height: 620px;
        margin: 0 0.5rem;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        position: relative;
    }

    &-content {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        flex-direction: column;
        overflow: hidden;
    }
}

.message {
    &-header {
        text-align: center;
        border-bottom: 1px solid #eee;
        padding-top: 0.75rem;
        padding-bottom: 0.5rem;
        flex-shrink: 0;

        h4 {
            margin: 0;
            font-size: 0.95rem;
            font-weight: 600;
            line-height: 1;
            color: #333;
        }

        .phone-number {
            color: #666;
            font-size: 0.8rem;
        }
    }

    &-content {
        flex: 1;
        padding: 1rem;
        overflow-y: auto;
        position: relative;

        .v-input,
        .v-btn,
        a,
        button,
        input,
        textarea,
        select {
            -webkit-user-select: text;
            -moz-user-select: text;
            -ms-user-select: text;
            user-select: text;
            cursor: auto;
        }

        &::-webkit-scrollbar {
            width: 3px;
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background: rgba(0, 0, 0, 0.2);
            border-radius: 3px;
            transition: background 0.2s ease;
        }

        &::-webkit-scrollbar-thumb:hover {
            background: rgba(0, 0, 0, 0.4);
        }

        &::-webkit-scrollbar-track {
            background: transparent;
        }

        scrollbar-width: thin;
        scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
    }
}

.browser {
    &-header {
        flex-shrink: 0;
        border-bottom: 1px solid #e0e0e0;
        padding: 0.5rem;

        .browser-controls {
            display: flex;
            align-items: center;
            gap: 0.375rem;

            .address-bar {
                position: relative;
                flex: 1;
                min-width: 0;
                background: #f8f8f8;
                border-radius: 16px;
                padding: 0.375rem 0.625rem;
                display: flex;
                align-items: center;
                gap: 0.375rem;

                .lock-icon {
                    position: absolute;
                    top: 50%;
                    left: 0.5rem;
                    transform: translateY(-50%);
                    color: #666;
                    flex-shrink: 0;
                }

                .url {
                    font-size: 0.75rem;
                    color: #333;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    min-width: 0;
                    flex: 1;
                    text-align: center;
                    padding: 0 1rem;
                }
            }
        }
    }

    &-content {
        flex: 1;
        padding: 0;
        overflow-y: auto;
        overflow-x: hidden;
        background: white;
        overscroll-behavior: contain;

        .v-input,
        .v-btn,
        a,
        button,
        input,
        textarea,
        select {
            -webkit-user-select: text;
            -moz-user-select: text;
            -ms-user-select: text;
            user-select: text;
            cursor: auto;
        }

        &::-webkit-scrollbar {
            width: 3px;
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background: rgba(0, 0, 0, 0.2);
            border-radius: 3px;
            transition: background 0.2s ease;
        }

        &::-webkit-scrollbar-thumb:hover {
            background: rgba(0, 0, 0, 0.4);
        }

        &::-webkit-scrollbar-track {
            background: transparent;
        }

        scrollbar-width: thin;
        scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
    }
}

// mobile-sized inputs
:deep(.mobile-form-container) {
    width: 100%;
    max-width: 100%;
    padding: 0.75rem;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    line-height: 1.4;

    .form-title {
        font-size: 1.1rem;
        font-weight: 600;
        margin: 0 0 0.5rem 0;
        text-align: center;
        color: #333;
    }

    .v-btn {
        text-transform: none;
        letter-spacing: normal;

        &.v-btn--outlined {
            border-width: 1.5px;
        }
    }

    .v-icon {
        &.v-icon--left {
            margin-right: 0.375rem;
        }
    }

    .mobile-actions {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
        margin-bottom: 1rem;

        .action-btn {
            font-size: 0.85rem;
            font-weight: 500;
            height: 44px;
            border-radius: 0.25rem;

            &.submit-btn {
                font-weight: 600;
            }
        }
    }

    .mobile-checkbox {
        margin: 0;
        padding: 0;

        .v-input--selection-controls__input {
            margin-right: 2px;
            align-self: flex-start;
        }

        .v-input--selection-controls__ripple {
            width: 32px;
            height: 32px;
        }

        .checkbox-label {
            font-size: 0.75rem;
            line-height: 1.3;
            color: #555;
        }
    }

    .mobile-input {
        margin-bottom: 0.75rem;

        &:last-of-type {
            margin-bottom: 0;
        }

        .v-input__control {
            min-height: 44px;
        }

        .v-text-field__details {
            margin-top: 0.25rem;
            margin-bottom: 0;
            padding-top: 0;
        }

        .v-input__slot {
            padding: 0 0.75rem;
            font-size: 0.85rem;
        }

        .v-label {
            font-size: 0.8rem;
        }

        .v-text-field__slot input {
            font-size: 0.85rem;
        }
    }

    .v-textarea {
        .v-input__control {
            min-height: auto;
        }

        .v-input__slot {
            padding: 0.75rem;
        }

        textarea {
            font-size: 0.85rem;
            line-height: 1.4;
            font-family: inherit;
        }

        .v-label {
            top: 0.75rem;
            left: 0.75rem;
        }

        .v-label-active {
            top: 0.25rem;
            font-size: 0.75rem;
        }
    }

    .v-input__details {
        min-height: auto;
        padding-top: 0.25rem;
    }

    .v-text-field--outlined fieldset {
        border-color: #dee2e6;
    }

    .v-input--checkbox .v-input__control {
        min-height: auto;
    }

    .v-input--checkbox .v-input__slot {
        margin-bottom: 0;
    }

    .v-input--selection-controls.v-input--dense .v-input--selection-controls__ripple {
        left: -11px;
    }
}

.phone-slide-enter-active,
.phone-slide-leave-active {
    transition: all 0.4s ease-in-out;
}

.phone-slide-enter-from {
    opacity: 0;
    transform: translateX(100%);
}

.phone-slide-leave-to {
    opacity: 0;
    transform: translateX(-100%);
}

.phone-slide-enter-to,
.phone-slide-leave-from {
    opacity: 1;
    transform: translateX(0);
}

.browser-content-slide-enter-active,
.browser-content-slide-leave-active {
    transition: all 0.35s ease-out;
}

.browser-content-slide-enter-from {
    opacity: 0.2;
    transform: translateX(100%);
}

.browser-content-slide-leave-to {
    opacity: 0.2;
    transform: translateX(-100%);
}

.browser-content-slide-enter-to,
.browser-content-slide-leave-from {
    opacity: 1;
    transform: translateX(0);
}
</style>
